import {
  err,
  ok,
  parsePeerTubeSearchResults,
  peerTubeHlsPlaylistUrl,
  peerTubeSearchUrl,
  peerTubeVideoDetailsUrl,
  type AbortLike,
  type PeerTubeSearchResult,
  type PeerTubeSettings,
  type Result,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { readJsonBody } from './json';
import type { ApiProblem } from './problem';

/**
 * The PeerTube fallback's two lookups, both through the shared transport — one retry policy and one
 * error classification for every request, SponsorBlock/DeArrow's own reasoning.
 */

/** The search hit list for a title — empty when the instance answered nothing usable. */
export async function fetchPeerTubeSearchResults(
  platform: Platform,
  settings: PeerTubeSettings,
  title: string,
  signal?: AbortLike,
): Promise<Result<readonly PeerTubeSearchResult[], ApiProblem>> {
  const url = peerTubeSearchUrl(settings, title);
  if (url === null) return ok([]);

  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) return err({ kind: 'transport', error: response.error });

  const body = readJsonBody(response.value.body);
  if (!body.ok) return err(body.error);
  return ok(parsePeerTubeSearchResults(body.value));
}

/**
 * The matched video's HLS master playlist URL — the "integrated" mode's Shaka source. `null` when
 * the video has none yet (still transcoding) rather than an error: a reader who matched a video
 * that cannot stream should see "no match" the same way a search with zero hits does.
 *
 * `instanceHost` is the *matched result's own* host (`PeerTubeSearchResult.instanceHost`), not
 * `settings.apiUrl` — a federated hit's record lives on the instance that actually hosts it.
 */
export async function fetchPeerTubeHlsPlaylistUrl(
  platform: Platform,
  instanceHost: string,
  uuid: string,
  signal?: AbortLike,
): Promise<Result<string | null, ApiProblem>> {
  const url = peerTubeVideoDetailsUrl(instanceHost, uuid);
  if (url === null) return ok(null);

  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) return err({ kind: 'transport', error: response.error });

  const body = readJsonBody(response.value.body);
  if (!body.ok) return err(body.error);
  return ok(peerTubeHlsPlaylistUrl(body.value));
}
